/**
 * 저장된 «장소 글자»에서 지도에 쓸 «검색어»를 뽑는다 — 없으면 null(지도를 안 그린다).
 *
 * ## 왜 이 함수가 이 기능의 «결정»인가
 *
 * 일정에는 좌표가 **저장되지 않는다**. `CalendarEvent` 에는 `location String? @db.VarChar(200)`
 * «글자 하나»뿐이고, 좌표 칸은 0개다(2026-09-29 실측). 장소 고르개(`LocationPicker`)가 고를 때는
 * 좌표를 알지만(`place.x`·`place.y`) 그것을 화면 안에만 두고 **글자만 올려보낸다.**
 * ⇒ 그래서 상세보기에서 지도를 그리려면 그 글자를 **다시 찾아야(지오코딩)** 한다.
 *
 * 그런데 사람이 손으로 적은 글자도 같은 칸에 들어온다 — 「3층 회의실」·「온라인」·「미정」.
 * 그런 것을 그대로 검색에 넣으면 **엉뚱한 곳에 핀이 꽂힌다.** 지도가 «없는» 것보다 나쁘다.
 * ⇒ ★ 그래서 이 함수가 「찾아볼 만한 글자인가」를 가른다. 그것이 이 기능의 결정 전부다.
 *
 * ## 고르개가 만드는 글자의 «꼴» (LocationPicker.handleSelect)
 *
 * ```
 * const addr  = place.road_address_name || place.address_name;
 * const label = place.place_name && addr ? `${place.place_name} (${addr})` : (place.place_name || addr);
 * ⇒ 「강남역 2호선 (서울 강남구 강남대로 지하 396)」   ← 거의 다 이 꼴
 *   「강남역 2호선」 · 「서울 강남구 …」                 ← 한쪽만 있을 때
 * ```
 *
 * ## 규칙 — 셋
 *
 * ```
 * ① 「이름 (주소)」 꼴이면 → «괄호 안»을 쓴다
 *    고르개가 카카오에서 «받아» 넣은 주소다. 이름보다 정확하고, 동명이 여럿인 곳에서도 안 흔들린다
 * ② 괄호가 없고 «주소처럼» 보이면 → 그 글자를 그대로 쓴다
 *    주소처럼 = 행정구역 낱말(시·도·군·구·읍·면·동·리·로·길) + 숫자
 * ③ 그 밖 → null. 지도를 «안 그린다»
 *    「3층 회의실」(숫자는 있지만 행정구역이 없다) · 「온라인」 · 빈 글자
 * ```
 *
 * ⚠ ②의 「숫자」 조건 때문에 「강남역」처럼 **번지 없는 이름**은 null 이 된다. 일부러 좁게 잡았다 —
 *   *지도가 없는 것*보다 *엉뚱한 핀*이 나쁘기 때문이다. 고르개로 고른 장소는 ①로 다 걸린다.
 *   ⇒ 넓힐 조건(재는 법): 「괄호 없이 저장된 장소가 얼마나 되나」를 운영에서 재고,
 *     그 비율이 «최근 100건 중 20건»을 넘으면 ②를 넓힌다(그 전에는 좁은 채로 둔다).
 *     ⛔ 「한 건이라도 나오면」으로 적지 말 것 — 그런 잣대는 우연 한 번에 열리고 다시 안 닫힌다.
 */

/** 행정구역·도로 낱말 — 「주소처럼 보이나」의 첫 조건. */
const ADMIN_WORD = /(특별시|광역시|특별자치시|특별자치도|[가-힣]+시|[가-힣]+도|[가-힣]+군|[가-힣]+구|[가-힣]+읍|[가-힣]+면|[가-힣]+동|[가-힣]+리|[가-힣]+로|[가-힣]+길)(\s|$)/;

/** 「이름 (주소)」 — 맨 뒤 괄호 한 쌍만 본다(이름 안에 괄호가 있어도 안 흔들리게). */
const LABEL_WITH_ADDR = /^(.*\S)\s*\(([^()]+)\)$/;

function looksLikeAddress(s: string): boolean {
  return ADMIN_WORD.test(s) && /\d/.test(s);
}

/**
 * @returns 지도에 쓸 검색어, 또는 null(= 지도를 그리지 않는다).
 */
export function locationToQuery(location: string | null | undefined): string | null {
  const raw = (location ?? '').trim();
  if (!raw) return null;

  const m = raw.match(LABEL_WITH_ADDR);
  // ⚠ `noUncheckedIndexedAccess` — 잡힌 그룹도 타입은 `string | undefined` 다.
  //   시험은 초록인데 `tsc -p tsconfig.app.json` 만 빨개지는 자리라 기본값을 함께 둔다.
  if (m) {
    const inner = (m[2] ?? '').trim();
    // 괄호 «안»이 주소가 아닐 수도 있다(「팀 회의(주간)」). 그때는 괄호 밖을 다시 본다.
    if (looksLikeAddress(inner)) return inner;
    const outer = (m[1] ?? '').trim();
    return looksLikeAddress(outer) ? outer : null;
  }

  return looksLikeAddress(raw) ? raw : null;
}
